import {
  BorderColorHC,
  BorderColorReactSelect,
  DarkWhiteColor,
  HeaderBG,
} from "@/components/colors";
import { getHexOpacity } from "@/components/utils/common";
import { ReactSelectListType } from "@/lib/types";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  useColorModeValue,
  useToken,
} from "@chakra-ui/react";
import Select, { type OptionProps } from "react-select";

type Props = {
  values?: null | string;
  accessor: string;
  label: string;
  errors: any;
  touched: any;
  options: Array<ReactSelectListType>;
  isSubmitting: boolean;
  isLoading?: boolean;
  setFieldValue: (accessor: string, value: any) => void;
};

const ReactSelectField = ({
  accessor,
  label,
  touched,
  errors,
  options: items,
  isLoading = false,
  values,
  isSubmitting,
  setFieldValue,
}: Props) => {
  return (
    <FormControl
      id={accessor}
      isInvalid={
        !!touched[accessor! as string] && !!errors[accessor! as string]
      }
    >
      <FormLabel fontSize="sm" htmlFor={accessor! as string}>
        {label}
      </FormLabel>
      <Select
        styles={ReactSelectStyles()}
        isLoading={isLoading || isSubmitting}
        isDisabled={isLoading || isSubmitting}
        placeholder={isLoading ? "Loading..." : "Select"}
        value={
          values
            ? items.map((item) => item.options.find((i) => i.value === values))
            : null
        }
        options={items}
        onChange={(item) => {
          if (item) {
            setFieldValue(`${accessor}`, item.value);
          }
        }}
      />
      <FormErrorMessage>{errors[accessor! as string]}</FormErrorMessage>
    </FormControl>
  );
};

export default ReactSelectField;

export function cssValueToNumber(cssValue: string) {
  const match = cssValue.match(/(\d+)(\.\d+)?/);
  if (match) {
    return parseFloat(match[0]);
  }
  return 0;
}

export function ReactSelectStyles(
  cursor: string = "pointer",
  borderColor: string = "",
  ignoreHeightRestriction: boolean = false
) {
  const [accentColor] = useToken("colors", ["accentColor"]);
  const [highContrast] = useToken("colors", ["highContrast"]);
  const [fontSize] = useToken("fontSizes", ["md"]);
  const disabledBg = useColorModeValue("#e2e8f0", "#334155");
  if (borderColor === "") {
    borderColor = BorderColorReactSelect();
  }

  const fsNumber = cssValueToNumber(fontSize);

  let borderWidth = 1;
  if (highContrast && typeof highContrast === "boolean") {
    borderColor = BorderColorHC();
    borderWidth = 2;
  }

  const fontColor = DarkWhiteColor();
  const background = HeaderBG();
  let height = `${fsNumber * 2.5}px`;

  let selectFontSize = `${fsNumber - 1.75}px`;

  if (ignoreHeightRestriction) {
    height = "auto";
  }

  return {
    control: (provided: any) => {
      return {
        ...provided,
        borderRadius: "none",
        fontSize: selectFontSize,
        background: "transparent",
        fontColor: "#000",
        color: "#000",
        border: `solid ${borderWidth}px  ${borderColor}`,
        cursor,
        //
        height,
        minHeight: height,
        "&:hover": { borderColor: "#4096ff" },
      };
    },
    noOptionsMessage: (provided: any) => ({
      ...provided,
      fontSize: selectFontSize,
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      height,
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      //
      height,
      "&:hover": { borderColor: "#4096ff" },
    }),
    input: (provided: any) => ({
      ...provided,
      color: fontColor,
    }),

    singleValue: (provided: any) => ({
      ...provided,
      color: fontColor,
    }),

    option: (provided: any, props: OptionProps) => {
      const { isFocused, isSelected } = props;
      let background = "transparent";
      let color = fontColor;

      if (isFocused) {
        background = getHexOpacity(accentColor, 50);
        color = "#fff";
      }
      if (isSelected) {
        background = accentColor;
        color = "#fff";
      }
      if (props.isDisabled) {
        background = disabledBg;
        cursor = "not-allowed";
      } else {
        cursor = "pointer";
      }

      return {
        ...provided,
        fontSize: selectFontSize,
        color,
        background,
        cursor,
      };
    },
    groupHeading: (_provided: any, b: any) => {
      const site = b.data.site;
      return {
        color: fontColor,
        fontSize: selectFontSize,
        fontWeight: site ? 600 : 700,
        borderTop: "solid 1px #d1d5db",
        paddingLeft: "6px",
        paddingBottom: "6px",
        paddingTop: "6px",
      };
    },
    menu: (props) => {
      return {
        ...props,
        background,
        zIndex: 10000,
      };
    },
    // container: (baseStyles) => ({ ...baseStyles, width: "max-content" }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  };
}
